import { countries } from "../components/CountryName";
import React, { useState } from 'react';
import {
    User, Calendar, MapPin, Globe, Languages, Image as ImageIcon, Loader2, Edit3, ChevronDown, X
} from 'lucide-react';
import { ToastContainer } from "react-toastify";

const BasicProfileForm = ({ previewData, setPreviewData, loading, errors, formData, handleChange, setFormData, handleUpload, formatDate }) => {

    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all bg-gray-50/50 hover:bg-white text-sm sm:text-base";
    const selectClass = "w-full border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all bg-gray-50/50 hover:bg-white text-sm sm:text-base appearance-none cursor-pointer";

    const handleLocalUpload = async (file) => {
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            alert("File must be under 2MB");
            return;
        }

        setUploading(true);
        try {
            await handleUpload(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="w-full">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={true} />

            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 overflow-hidden relative">

                {/* Loading Overlay */}
                {(loading || uploading) && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                        <Loader2 className="animate-spin text-blue-600" size={40} />
                        <p className="text-sm text-gray-600 mt-3 font-medium">{uploading ? "Uploading..." : "Loading..."}</p>
                    </div>
                )}

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-5 sm:p-6 md:p-8 text-white">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Personal Profile</h1>
                            <p className="text-blue-100 text-xs sm:text-sm mt-1 opacity-90">Manage your professional identity and details</p>
                        </div>
                        {previewData?.status === "reject" && (
                            <button
                                onClick={() => setPreviewData(null)}
                                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                            >
                                <Edit3 size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="p-4 sm:p-6 md:p-8">
                    {!previewData ? (
                        <form className="space-y-6 sm:space-y-8">

                            {/* Section 1: Basic Info */}
                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                    <FormField label="First Name" icon={User} error={errors.name} required>
                                        <input
                                            name="name"
                                            value={formData.name || ""}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="Enter first name"
                                        />
                                    </FormField>
                                    <FormField label="Surname" icon={User} error={errors.surname} required>
                                        <input
                                            name="surname"
                                            value={formData.surname || ""}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="Enter surname"
                                        />
                                    </FormField>
                                    <FormField label="Date of Birth" icon={Calendar} error={errors.dateOfBirth} required>
                                        <input
                                            type="date"
                                            value={formData.dateOfBirth ? formData.dateOfBirth.toISOString().split('T')[0] : ''}
                                            onChange={(e) => {
                                                const dateVal = e.target.value ? new Date(e.target.value) : null;
                                                setFormData((p) => ({ ...p, dateOfBirth: dateVal }));
                                            }}
                                            className={inputClass}
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                    </FormField>
                                    <FormField label="Gender" icon={User} error={errors.gender} required>
                                        <div className="relative">
                                            <select
                                                name="gender"
                                                value={formData.gender || ""}
                                                onChange={handleChange}
                                                className={selectClass}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                                <option value="prefer_not_to_say">Prefer Not to Say</option>
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </FormField>
                                </div>
                            </div>

                            {/* Section 2: Origin & Identity */}
                            <div>
                                <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                                    <Globe size={18} className="text-blue-600" />
                                    Origin & Identity
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                    <FormField label="Place of Birth" icon={MapPin} error={errors.placeOfBirth} required>
                                        <input
                                            name="placeOfBirth"
                                            value={formData.placeOfBirth || ""}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="City, Country"
                                        />
                                    </FormField>
                                    <FormField label="Primary Citizenship" icon={Globe} required>
                                        <input
                                            name="citizenship"
                                            value={formData.citizenship || ""}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="e.g. Indian"
                                        />
                                    </FormField>
                                    <FormField label="Mother Tongue" icon={Languages}>
                                        <input
                                            name="motherTongue"
                                            value={formData.motherTongue || ""}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="e.g. Hindi, Punjabi"
                                        />
                                    </FormField>
                                    <FormField label="Religion">
                                        <div className="relative">
                                            <select
                                                name="religion"
                                                value={formData.religion || ""}
                                                onChange={handleChange}
                                                className={selectClass}
                                            >
                                                <option value="">Select Religion</option>
                                                <option>Hindu</option>
                                                <option>Muslim</option>
                                                <option>Christian</option>
                                                <option>Sikh</option>
                                                <option>Bodh</option>
                                                <option>Jain</option>
                                                <option>Other</option>
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </FormField>
                                </div>
                            </div>

                            {/* Section 3: Additional Details */}
                            <div>
                                <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                                    <MapPin size={18} className="text-blue-600" />
                                    Additional Details
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                    <FormField label="Dual Citizenship?">
                                        <div className="relative">
                                            <select
                                                name="otherCitizen"
                                                value={formData.otherCitizen || "No"}
                                                onChange={handleChange}
                                                className={selectClass}
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </FormField>

                                    {formData.otherCitizen === "Yes" && (
                                        <FormField label="Second Country" icon={Globe}>
                                            <div className="relative">
                                                <select
                                                    name="otherCountry"
                                                    value={formData.otherCountry || ""}
                                                    onChange={handleChange}
                                                    className={selectClass}
                                                >
                                                    <option value="">Select Country</option>
                                                    {countries.map((c, i) => <option key={i} value={c}>{c}</option>)}
                                                </select>
                                                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            </div>
                                        </FormField>
                                    )}

                                    <FormField label="Height" error={errors.height}>
                                        <div className="relative">
                                            <select
                                                name="height"
                                                value={formData.height || ""}
                                                onChange={handleChange}
                                                className={selectClass}
                                            >
                                                <option value="">Select Height</option>
                                                {HEIGHT_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </FormField>

                                    <FormField label="Caste Certificate" icon={ImageIcon}>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                            <label className="flex-1 cursor-pointer bg-white border-2 border-dashed border-gray-300 hover:border-blue-500 rounded-xl p-3 text-center transition-all group w-full">
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={(e) => handleLocalUpload(e.target.files[0])}
                                                />
                                                <div className="flex flex-col items-center gap-1">
                                                    <ImageIcon size={20} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                                                    <span className="text-xs text-gray-500 group-hover:text-blue-600">
                                                        {formData.casteImageUrl ? "Change Image" : "Click to Upload"}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400">Max 2MB</span>
                                                </div>
                                            </label>
                                            {(formData.casteImageUrl || previewImage) && (
                                                <div className="relative group/img">
                                                    <img
                                                        src={previewImage || formData.casteImageUrl}
                                                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                                                        alt="caste preview"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData((p) => ({ ...p, casteImageUrl: "" }));
                                                            setPreviewImage(null);
                                                        }}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </FormField>
                                </div>
                            </div>

                            {/* Form Footer */}
                            <div className="pt-4 border-t border-gray-100">
                                <p className="text-xs text-gray-400 text-center">
                                    Fields marked with <span className="text-red-500">*</span> are required
                                </p>
                            </div>
                        </form>
                    ) : (
                        /* Preview Mode */
                        <div className="animate-in fade-in duration-300">
                            {/* Profile Header */}
                            <div className="text-center mb-6 sm:mb-8">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                                    <User size={36} className="text-white" />
                                </div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                                    {previewData.name} {previewData.surname}
                                </h2>
                                {previewData?.uci && (
                                    <p className="text-gray-500 text-xs sm:text-sm mt-1 font-mono">UCI: {previewData.uci}</p>
                                )}
                                <div className="flex flex-wrap justify-center gap-2 mt-3">
                                    <span className="text-xs sm:text-sm text-gray-600 capitalize bg-gray-100 px-3 py-1 rounded-full">
                                        {previewData.gender}
                                    </span>
                                    {previewData.religion && (
                                        <span className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                            {previewData.religion}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Info Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                <InfoCard label="Date of Birth" value={formatDate(previewData.dateOfBirth)} />
                                <InfoCard label="Place of Birth" value={previewData.placeOfBirth} />
                                <InfoCard label="Citizenship" value={previewData.citizenship} />
                                <InfoCard
                                    label="Dual Citizenship"
                                    value={previewData?.otherCitizenship?.hasOther ? previewData.otherCitizenship.country : "No"}
                                />
                                <InfoCard label="Mother Tongue" value={previewData.motherTongue || "N/A"} />
                                <InfoCard
                                    label="Height"
                                    value={previewData.heightInInch ? `${Math.floor(previewData.heightInInch / 12)}' ${previewData.heightInInch % 12}"` : "N/A"}
                                />
                                <InfoCard label="Application Status" value={previewData.status} isStatus />
                            </div>

                            {/* Caste Document */}
                            {previewData.caste && (
                                <div className="mt-6 sm:mt-8 text-center border-t pt-6">
                                    <p className="text-xs text-gray-400 mb-3 uppercase tracking-wider">Caste Certificate</p>
                                    <img
                                        src={previewData.caste}
                                        alt="caste certificate"
                                        className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-xl mx-auto border shadow-md hover:shadow-lg transition-shadow"
                                    />
                                </div>
                            )}

                            {/* Edit Button */}
                            {previewData.status === "reject" && (
                                <div className="text-center mt-6 sm:mt-8">
                                    <button
                                        onClick={() => setPreviewData(null)}
                                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-md"
                                    >
                                        <Edit3 size={16} /> Edit Details
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Generate height options
const generateHeights = () => {
    const heights = [];
    for (let inches = 48; inches <= 84; inches++) {
        const feet = Math.floor(inches / 12);
        const inch = inches % 12;
        const cm = Math.round(inches * 2.54);
        heights.push({ value: inches, label: `${feet}' ${inch}" (${cm} cm)` });
    }
    return heights;
};

const HEIGHT_OPTIONS = generateHeights();

// Form Field Component
const FormField = React.memo(({ label, icon: Icon, children, error, required }) => (
    <div className="flex flex-col gap-1.5 w-full">
        <label className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
            {Icon && <Icon size={14} className="text-blue-600" />}
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
        {error && (
            <p className="text-red-500 text-[10px] sm:text-xs mt-0.5 flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {error}
            </p>
        )}
    </div>
));

// Info Card Component
const InfoCard = ({ label, value, isStatus }) => {
    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case "approved": return "bg-green-100 text-green-700";
            case "pending": return "bg-yellow-100 text-yellow-700";
            case "under_review": return "bg-blue-100 text-blue-700";
            case "rejected": return "bg-red-100 text-red-700";
            default: return "bg-gray-100 text-gray-600";
        }
    };

    return (
        <div className="p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl hover:bg-gray-100 transition-all duration-200 hover:shadow-sm">
            <p className="text-[10px] sm:text-[11px] text-gray-400 uppercase tracking-wider font-medium">
                {label}
            </p>
            {isStatus ? (
                <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full capitalize ${getStatusStyle(value)}`}>
                    {value?.replace(/_/g, " ") || "N/A"}
                </span>
            ) : (
                <p className="font-semibold text-xs sm:text-sm text-gray-800 mt-1 break-words">
                    {value || "N/A"}
                </p>
            )}
        </div>
    );
};

export default React.memo(BasicProfileForm);